import React, { useState } from 'react';
import { Box, FormControl, FormControlLabel, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import Btn from 'components/button';
import { CssTextField } from 'pages/reference-implementation/components/select';
import riStyle, { RIStyles, CustomRadioGroup, CustomRadio } from 'pages/reference-implementation/Style';
import { commonStyles as style } from 'assets/style';
import deleteIcon from 'assets/images/delete_icon.svg';
import addIcon from 'assets/images/add_icon.svg';
import HelpImg from 'pages/provisioning/_components/helpImg';
import { validateS3FolderName } from 'pages/reference-implementation/provisioning/RI003AWS/utils/formValidation';

const MAX_S3_FOLDERS = 5;

const s3FolderNamingHelpText = (
    <span>
        S3 Folder naming rules (Object Key Prefixes):
        <br />
        • Up to 255 characters per folder name (total object key up to 1024 characters)
        <br />
        • Allowed characters: A-Z, a-z, 0-9, periods (.), hyphens (-), tildes (~), underscores (_), equal signs (=), colons (:), forward slashes (/)
        <br />
        • Case-sensitive (MyFolder ≠ myfolder)
        <br />
        • Single forward slashes (/) allowed for hierarchical paths (e.g., documents/images)
        <br />
        • Cannot start or end with period (.) or forward slash (/)
        <br />
        • No consecutive slashes (//) allowed
        <br />
        • Empty folder names not allowed
        <br />
        • Maximum 254 path segments allowed
        <br />• Must be unique within the bucket
    </span>
);

const AWSS3 = (props) => {
    const { resource, handleDrawerAction } = props;

    const [formData, setFormData] = useState({
        isS3FolderRequired: resource?.configureData?.isS3FolderRequired || 'no',
        s3Folders: resource?.configureData?.s3Folders?.length ? resource.configureData.s3Folders : [''],
    });
    const [helperText, setHelperText] = useState(new Array(formData.s3Folders.length).fill(''));
    const [errors, setErrors] = useState({});

    const validateFolders = (folders) => {
        const updatedHelperTexts = [];
        let hasErrors = false;

        folders.forEach((currentFolder, idx) => {
            updatedHelperTexts[idx] = '';
            if (currentFolder.trim() === '') {
                return;
            }
            const { isValid: isFolderValid, message } = validateS3FolderName(currentFolder);
            if (!isFolderValid) {
                updatedHelperTexts[idx] = message;
                hasErrors = true;
                return;
            }
            const isDuplicate = folders.some((folder, otherIdx) => otherIdx !== idx && folder.trim() === currentFolder.trim() && folder.trim() !== '');
            if (isDuplicate) {
                updatedHelperTexts[idx] = 'S3 Folder names should be unique.';
                hasErrors = true;
            }
        });

        setHelperText(updatedHelperTexts);
        return !hasErrors;
    };

    const handleRadioChange = (value) => {
        setFormData({ ...formData, isS3FolderRequired: value });
    };

    const addS3Folder = () => {
        if (formData.s3Folders.length < MAX_S3_FOLDERS) {
            const newFolders = [...formData.s3Folders, ''];
            setFormData({ ...formData, s3Folders: newFolders });
            validateFolders(newFolders);
        }
    };

    const removeS3Folder = (removeIndex) => {
        if (formData.s3Folders.length > 1) {
            const updatedFolders = formData.s3Folders.filter((_, idx) => idx !== removeIndex);
            setFormData({ ...formData, s3Folders: updatedFolders });
            validateFolders(updatedFolders);
        }
    };

    const updateS3Folder = (value, updateIndex) => {
        const updatedFolders = [...formData.s3Folders];
        updatedFolders[updateIndex] = value;
        setFormData({ ...formData, s3Folders: updatedFolders });
        validateFolders(updatedFolders);
    };

    const validateForm = () => {
        const newErrors = {};
        if (formData.isS3FolderRequired === 'yes') {
            const validFolders = formData.s3Folders.filter((folder) => folder.trim() !== '');
            const foldersValid = validateFolders(formData.s3Folders);
            if (validFolders.length === 0 || !foldersValid) {
                newErrors.s3Folders = { type: 'required', message: 'At least one valid S3 Folder name is required.' };
            }
        }
        setErrors(newErrors);
        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    };

    const handleSubmit = () => {
        const checkFormValid = validateForm();
        const payload = {
            isS3FolderRequired: formData.isS3FolderRequired,
            s3Folders: formData.isS3FolderRequired === 'yes' ? formData.s3Folders.filter((folder) => folder.trim() !== '') : [],
        };
        handleDrawerAction('save', payload, checkFormValid);
    };

    const hasValidFolders =
        formData.isS3FolderRequired !== 'yes' || (formData.s3Folders.some((folder) => folder.trim() !== '') && helperText.every((text) => text === ''));

    return (
        <Box style={RIStyles.tags.wrapper} className="ri-layout">
            <Box sx={RIStyles.tags.contentWrapper}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography sx={riStyle?.form?.accessMgmt?.label} mb={1}>
                            Do you required S3 Folder Creation ? *
                        </Typography>
                        <FormControl data-testid="is-s3-folder-required-radio">
                            <CustomRadioGroup
                                aria-label="isS3FolderRequired"
                                name="isS3FolderRequiredRadioBtnGroup"
                                row
                                value={formData.isS3FolderRequired}
                                onChange={(event, value) => handleRadioChange(value)}
                            >
                                <FormControlLabel value="yes" data-testid="s3FolderRequiredYes" control={<CustomRadio />} label="Yes" />
                                <FormControlLabel
                                    value="no"
                                    sx={{ marginLeft: '60px' }}
                                    data-testid="s3FolderRequiredNo"
                                    control={<CustomRadio />}
                                    label="No"
                                />
                            </CustomRadioGroup>
                        </FormControl>
                    </Grid>
                    {formData.isS3FolderRequired === 'yes' && (
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{ color: '#0047BA', fontSize: '14px', fontWeight: 'bold', mr: 1 }}>S3 Folder Names *</Typography>
                                <HelpImg title={s3FolderNamingHelpText} maxWidth="600px" />
                            </Box>
                            {formData.s3Folders.map((folder, idx) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Box key={idx} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <CssTextField
                                        size="small"
                                        sx={{ flex: 1, width: '100%' }}
                                        autoComplete="off"
                                        label="S3 Folder Name *"
                                        error={!!helperText[idx]}
                                        helperText={helperText[idx] || ''}
                                        value={folder || ''}
                                        onChange={(e) => updateS3Folder(e.target.value, idx)}
                                        data-testid={`s3-folder-${idx}`}
                                        placeholder="S3 Folder Name"
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                                        {formData.s3Folders.length > 1 && (
                                            <Box onClick={() => removeS3Folder(idx)} sx={{ cursor: 'pointer' }}>
                                                <img data-testid={`remove-btn-${idx}`} style={{ width: 16, height: 16 }} src={deleteIcon} alt="deleteIcon" />
                                            </Box>
                                        )}
                                        {idx === formData.s3Folders.length - 1 && formData.s3Folders.length < MAX_S3_FOLDERS && (
                                            <Box onClick={addS3Folder} sx={{ cursor: 'pointer' }}>
                                                <img data-testid="add-btn" style={{ width: 16, height: 16 }} src={addIcon} alt="addIcon" />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                            {errors?.s3Folders && (
                                <Typography color="error" variant="caption">
                                    {errors.s3Folders.message}
                                </Typography>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Box>
            <Box className="footer" sx={RIStyles.tags.actionWrapper} gap={2}>
                <Btn
                    className="custom-btn"
                    variant="contained"
                    sx={style.modalSaveButton}
                    onClick={() => handleSubmit()}
                    disabled={!hasValidFolders}
                    data-testid="save-btn"
                >
                    Save
                </Btn>
                <Btn
                    className="custom-btn"
                    variant="outlined"
                    sx={style.modalCancelButton}
                    onClick={() => handleDrawerAction('cancel')}
                    data-testid="cancel-btn"
                >
                    Cancel
                </Btn>
            </Box>
        </Box>
    );
};

AWSS3.propTypes = {
    resource: PropTypes.any.isRequired,
    handleDrawerAction: PropTypes.func.isRequired,
};

export default AWSS3;
